import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadLocale } from '../i18n';

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  availableLanguages: Array<{ code: string; name: string; nativeName: string }>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Initialize with stored language or default
    const storedLanguage = localStorage.getItem('pitto-language');
    return storedLanguage || i18n.language || 'en-US';
  });

  const availableLanguages = [
    { code: 'en-US', name: 'English', nativeName: 'English' },
    { code: 'zh-CN', name: 'Chinese', nativeName: '中文' },
    { code: 'ja-JP', name: 'Japanese', nativeName: '日本語' },
  ];

  // Only the active locale tree is bundled with the app, so a switch has to
  // fetch the new one before i18next is told about it — otherwise the UI paints
  // raw translation keys for as long as the request takes.
  const setLanguage = (language: string) => {
    localStorage.setItem('pitto-language', language);
    loadLocale(language).then(() => {
      i18n.changeLanguage(language);
      setCurrentLanguage(language);
    });
  };

  useEffect(() => {
    // Initialize i18n with stored language
    const storedLanguage = localStorage.getItem('pitto-language');
    if (storedLanguage && storedLanguage !== i18n.language) {
      loadLocale(storedLanguage).then(() => {
        i18n.changeLanguage(storedLanguage);
        setCurrentLanguage(storedLanguage);
      });
    }

    // Listen for language changes
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
